import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import { PrivacyProvider } from "@/context/PrivacyContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AccessibilityProvider, AccessibilityToolbar } from "@/components/AccessibilityToolbar";
import { CAAProvider } from "@/hooks/useCAA";
import CookieBanner from "@/components/CookieBanner";
import FeedbackWidget from "@/components/FeedbackWidget";
import { lazy, Suspense } from "react";

// Lazy-loaded pages for code splitting
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Repository = lazy(() => import("@/pages/Repository"));
const CreateExercise = lazy(() => import("@/pages/CreateExercise"));
const GenerateImage = lazy(() => import("@/pages/GenerateImage"));
const ActivityDetail = lazy(() => import("@/pages/ActivityDetail"));
const SharedActivities = lazy(() => import("@/pages/SharedActivities"));
const CreateBoardGame = lazy(() => import("@/pages/CreateBoardGame"));
const CreateDictation = lazy(() => import("@/pages/CreateDictation"));
const CreateUDA = lazy(() => import("@/pages/CreateUDA"));
const CreateMindMap = lazy(() => import("@/pages/CreateMindMap"));
const CreateAssessment = lazy(() => import("@/pages/CreateAssessment"));
const ClassManager = lazy(() => import("@/pages/ClassManager"));
const WeeklyPlanner = lazy(() => import("@/pages/WeeklyPlanner"));
const CreateFlashcards = lazy(() => import("@/pages/CreateFlashcards"));
const ChatHistory = lazy(() => import("@/pages/ChatHistory"));
const SOSSupplenza = lazy(() => import("@/pages/SOSSupplenza"));
const CreateNotice = lazy(() => import("@/pages/CreateNotice"));
const CivicPlanner = lazy(() => import("@/pages/CivicPlanner"));
const CreateLapbook = lazy(() => import("@/pages/CreateLapbook"));
const UserSettings = lazy(() => import("@/pages/UserSettings"));
const Community = lazy(() => import("@/pages/Community"));
const CalendarView = lazy(() => import("@/pages/CalendarView"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const StoryFactory = lazy(() => import("@/pages/StoryFactory"));
const CreateMinutes = lazy(() => import("@/pages/CreateMinutes"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const AnalyzeHomework = lazy(() => import("@/pages/AnalyzeHomework"));
const CreateGlossary = lazy(() => import("@/pages/CreateGlossary"));
const ImageActivity = lazy(() => import("@/pages/ImageActivity"));
const ImageActivityLanding = lazy(() => import("@/pages/ImageActivityLanding"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
// Wrapper components for named exports from LegalPages
const PrivacyPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.TermsPage })));
const SpainLanding = lazy(() => import("@/pages/SpainLanding"));

const LazyLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-gray-500 text-sm font-medium">Caricamento...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground font-medium">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<LazyLoading />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy-policy" element={<PrivacyPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/termini" element={<TermsPage />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/attivita-da-immagine" element={<ImageActivityLanding />} />
      <Route path="/es" element={<SpainLanding />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/repository"
        element={
          <ProtectedRoute>
            <Repository />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-exercise"
        element={
          <ProtectedRoute>
            <CreateExercise />
          </ProtectedRoute>
        }
      />
      <Route
        path="/generate-image"
        element={
          <ProtectedRoute>
            <GenerateImage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity/:id"
        element={
          <ProtectedRoute>
            <ActivityDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shared"
        element={
          <ProtectedRoute>
            <SharedActivities />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-board-game"
        element={
          <ProtectedRoute>
            <CreateBoardGame />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-dictation"
        element={
          <ProtectedRoute>
            <CreateDictation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-uda"
        element={
          <ProtectedRoute>
            <CreateUDA />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-mindmap"
        element={
          <ProtectedRoute>
            <CreateMindMap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-assessment"
        element={
          <ProtectedRoute>
            <CreateAssessment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-manager"
        element={
          <ProtectedRoute>
            <ClassManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/weekly-planner"
        element={
          <ProtectedRoute>
            <WeeklyPlanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-flashcards"
        element={
          <ProtectedRoute>
            <CreateFlashcards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat-history"
        element={
          <ProtectedRoute>
            <ChatHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sos-supplenza"
        element={
          <ProtectedRoute>
            <SOSSupplenza />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-notice"
        element={
          <ProtectedRoute>
            <CreateNotice />
          </ProtectedRoute>
        }
      />
      <Route
        path="/civic-planner"
        element={
          <ProtectedRoute>
            <CivicPlanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-lapbook"
        element={
          <ProtectedRoute>
            <CreateLapbook />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <UserSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <Community />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/story-factory"
        element={
          <ProtectedRoute>
            <StoryFactory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-minutes"
        element={
          <ProtectedRoute>
            <CreateMinutes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analyze-homework"
        element={
          <ProtectedRoute>
            <AnalyzeHomework />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-glossary"
        element={
          <ProtectedRoute>
            <CreateGlossary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/image-activity"
        element={
          <ProtectedRoute>
            <ImageActivity />
          </ProtectedRoute>
        }
      />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <PrivacyProvider>
              <AccessibilityProvider>
                <CAAProvider>
                  <AppRouter />
                  <AccessibilityToolbar />
                  <FeedbackWidget />
                  <CookieBanner />
                  <Toaster position="top-right" richColors />
                </CAAProvider>
              </AccessibilityProvider>
            </PrivacyProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
